<template><div><component-combinator component-name="DtText" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<code-example only-show="code" source-code='&lt;dt-text&gt; ... &lt;/dt-text&gt;'>
<dt-text> ... </dt-text>
</code-example>
<p class="d-docsite--paragraph">Use in place of manually applying Text Styles. Examples of manual application <strong>you should avoid</strong> include:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Applying Text Styles classes, e.g. <code v-pre>class=&quot;d-text-body--md&quot;</code></li>
<li class="d-docsite--list-element">Combinations of CSS Utilities, e.g. <code v-pre>class=&quot;d-fs-300 d-fw-semibold d-lh-300&quot;</code></li>
<li class="d-docsite--list-element">Custom CSS, e.g. <code v-pre>.foo { font: var(--dt-typography-body-md); }</code>.</li>
</ul>
<h3 id="guidance" tabindex="-1" class="d-docsite--header-3">Guidance <a class="header-anchor d-link d-docsite--link d-link" href="#guidance" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Prefer <code v-pre>DtText</code> over individual typography utility classes to keep implementations aligned with token updates.</li>
<li class="d-docsite--list-element">Use the default slot for rich content. The <code v-pre>text</code> prop provides a simple fallback string when no slot content is present.</li>
<li class="d-docsite--list-element">Choose the <code v-pre>as</code> prop to match the semantic HTML element (e.g., <code v-pre>h1</code>, <code v-pre>label</code>, <code v-pre>p</code>).</li>
<li class="d-docsite--list-element">All properties are optional, as they layer in on top of each other.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Replace multiple <code v-pre>d-</code> typography classes with a single <code v-pre>dt-text</code> instance.</li>
<li class="d-docsite--list-element">Pick the smallest <code v-pre>kind</code>/<code v-pre>size</code> combination that conveys the desired hierarchy.</li>
<li class="d-docsite--list-element">Use <code v-pre>tone</code> for semantic color tokens instead of standalone <code v-pre>d-fc-*</code> classes.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Mix <code v-pre>DtText</code> with conflicting typography utilities (e.g., <code v-pre>d-fs-*</code>).</li>
<li class="d-docsite--list-element">Render headings with non-heading tags (e.g., avoid <code v-pre>as=&quot;div&quot;</code> for top-level titles).</li>
<li class="d-docsite--list-element">Depend on the <code v-pre>text</code> prop when the content requires inline formatting; slot it instead.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="kind" tabindex="-1" class="d-docsite--header-3">Kind <a class="header-anchor d-link d-docsite--link d-link" href="#kind" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Declare the role of the content. Default will inherit styles from the parent.</p>
<code-example source-code='&lt;dt-text kind=&quot;headline&quot; as=&quot;span&quot;&gt;Headline&lt;/dt-text&gt;
&lt;dt-text kind=&quot;body&quot;&gt;Body&lt;/dt-text&gt;
&lt;dt-text kind=&quot;label&quot;&gt;Label&lt;/dt-text&gt;
&lt;dt-text kind=&quot;code&quot;&gt;Code&lt;/dt-text&gt;
&lt;dt-text&gt;Default (inherits)&lt;/dt-text&gt;'>
<dt-stack data-demo-wrapper gap="400" :direction="{ 'default': 'column', 'md': 'row' }" align="baseline">
  <dt-text kind="headline" as="span">Headline</dt-text>
  <dt-text kind="body">Body</dt-text>
  <dt-text kind="label">Label</dt-text>
  <dt-text kind="code">Code</dt-text>
  <dt-text>Default (inherits)</dt-text>
</dt-stack>
</code-example>
<h3 id="size" tabindex="-1" class="d-docsite--header-3">Size <a class="header-anchor d-link d-docsite--link d-link" href="#size" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">All kinds support <code v-pre>size</code> prop, but not all sizes are available for each kind. When <code v-pre>kind</code> is set, size defaults to <code v-pre>300</code> if not specified.</p>
<dt-stack class="d-w100p d-ba d-bar-300 d-of-auto">
  <table class="d-w100p d-table">
    <tr class="d-va-baseline">
      <th></th>
      <th v-for="s in textSizeColumns" :key="s" class="d-ta-center">
        <dt-text as="code" kind="code" size="100" class="d-docsite-code">{{ s }}</dt-text>
      </th>
    </tr>
    <tr v-for="kind in textSizeKinds" :key="kind.name" class="d-va-baseline">
      <th scope="row">
        <dt-text as="code" align="end" kind="code" size="100" class="d-docsite-code">{{ kind.name }}</dt-text>
      </th>
      <td v-for="s in textSizeColumns" :key="s" class="d-ta-center">
        <dt-text v-if="kind.sizes.includes(String(s))" :kind="kind.name" :as="kind.as" :size="s" :class="kind.class" :tone="kind.tone">Text</dt-text>
        <dt-text v-else tone="muted" kind="body" :size="200" title="not available">-</dt-text>
      </td>
    </tr>
  </table>
</dt-stack>
<code-example only-show="code" source-code='&lt;dt-text kind=&quot;{kind}&quot; :size=&quot;{size}&quot;&gt;....&lt;/dt-text&gt;'>
<dt-text kind="{kind}" :size="{size}">....</dt-text>
</code-example>
<h3 id="numeric" tabindex="-1" class="d-docsite--header-3">Numeric <a class="header-anchor d-link d-docsite--link d-link" href="#numeric" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The <code v-pre>numeric</code> prop applies styles that ensure that each number is set with consistent width, making them align properly when displayed together. Ideal for displaying aligned data such as phone numbers or numbers in a table.</p>
<code-example source-code='&lt;dt-text numeric&gt;(913) 555-3170&lt;/dt-text&gt;'>
<dt-stack direction="row" gap="200">
  <dt-stack>
    <dt-text kind="label" :size="200" tone="critical">Without numeric</dt-text>
    <dt-text>(913) 555-3170</dt-text>
    <dt-text>(908) 555-1111</dt-text>
    <dt-text>(805) 555-8413</dt-text>
    <dt-text>(816) 555-1203</dt-text>
    <dt-text>(886) 555-8888</dt-text>
  </dt-stack>
  <dt-stack>
    <dt-text kind="label" :size="200" tone="positive">With numeric</dt-text>
    <dt-text numeric>(913) 555-3170</dt-text>
    <dt-text numeric>(908) 555-1111</dt-text>
    <dt-text numeric>(805) 555-8413</dt-text>
    <dt-text numeric>(816) 555-1203</dt-text>
    <dt-text numeric>(886) 555-8888</dt-text>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="strength" tabindex="-1" class="d-docsite--header-3">Strength <a class="header-anchor d-link d-docsite--link d-link" href="#strength" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Override the font-weight of the text. Applies to any kind/size combination. If omitted, the default weight from the typography token is used.</p>
<code-example source-code='&lt;dt-text strength=&quot;{{strength}}&quot;&gt;...&lt;/dt-text&gt;'>
<dt-stack :direction="{ 'default': 'column', 'md': 'row' }" gap="200" class="d-fw-wrap">
  <dt-text strength="bold">Bold</dt-text>
  <dt-text strength="semibold">Semibold</dt-text>
  <dt-text strength="medium">Medium</dt-text>
  <dt-text strength="normal">Normal</dt-text>
</dt-stack>
</code-example>
<h3 id="density" tabindex="-1" class="d-docsite--header-3">Density <a class="header-anchor d-link d-docsite--link d-link" href="#density" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Override the line-height of the text. Applies to any kind/size combination. If omitted, the default line-height from the typography token is used.</p>
<code-example source-code='&lt;dt-text density=&quot;{{density}}&quot;&gt;...&lt;/dt-text&gt;'>
<dt-stack gap="100">
  <dt-text kind="body" as="p" density="100" class="d-bgc-moderate-opaque"><dt-text kind="code" as="code" :size="200" class="d-bgc-transparent">100</dt-text> The quick brown fox jumped over the lazy dog.</dt-text>
  <dt-text kind="body" as="p" density="200" class="d-bgc-moderate-opaque"><dt-text kind="code" as="code" :size="200" class="d-bgc-transparent">200</dt-text> The quick brown fox jumped over the lazy dog.</dt-text>
  <dt-text kind="body" as="p" density="300" class="d-bgc-moderate-opaque"><dt-text kind="code" as="code" :size="200" class="d-bgc-transparent">300</dt-text> The quick brown fox jumped over the lazy dog.</dt-text>
  <dt-text kind="body" as="p" density="400" class="d-bgc-moderate-opaque"><dt-text kind="code" as="code" :size="200" class="d-bgc-transparent">400</dt-text> The quick brown fox jumped over the lazy dog.</dt-text>
  <dt-text kind="body" as="p" density="500" class="d-bgc-moderate-opaque"><dt-text kind="code" as="code" :size="200" class="d-bgc-transparent">500</dt-text> The quick brown fox jumped over the lazy dog.</dt-text>
  <dt-text kind="body" as="p" density="600" class="d-bgc-moderate-opaque"><dt-text kind="code" as="code" :size="200" class="d-bgc-transparent">600</dt-text> The quick brown fox jumped over the lazy dog.</dt-text>
</dt-stack>
</code-example>
<h2 id="tone" tabindex="-1" class="d-docsite--header-2">Tone <a class="header-anchor d-link d-docsite--link d-link" href="#tone" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>tone</code> to declare the text's tone, which will map to a foreground color. By default, the tone is inherited from its parent.</p>
<code-example source-code='&lt;dt-text&gt;primary&lt;/dt-text&gt;
&lt;dt-text tone=&quot;secondary&quot;&gt;secondary&lt;/dt-text&gt;
&lt;dt-text tone=&quot;tertiary&quot;&gt;tertiary&lt;/dt-text&gt;
&lt;dt-text tone=&quot;muted&quot;&gt;muted&lt;/dt-text&gt;
&lt;dt-text tone=&quot;disabled&quot;&gt;disabled&lt;/dt-text&gt;
&lt;dt-text tone=&quot;placeholder&quot;&gt;placeholder&lt;/dt-text&gt;
&lt;dt-text tone=&quot;positive&quot;&gt;positive&lt;/dt-text&gt;
&lt;dt-text tone=&quot;positive-strong&quot;&gt;positive-strong&lt;/dt-text&gt;
&lt;dt-text tone=&quot;warning&quot;&gt;warning&lt;/dt-text&gt;
&lt;dt-text tone=&quot;critical&quot;&gt;critical&lt;/dt-text&gt;
&lt;dt-text tone=&quot;critical-strong&quot;&gt;critical-strong&lt;/dt-text&gt;
&lt;dt-text tone=&quot;info&quot;&gt;info&lt;/dt-text&gt;
&lt;dt-text tone=&quot;info-strong&quot;&gt;info-strong&lt;/dt-text&gt;'>
<dt-stack data-demo-wrapper class="d-py-100 d-px-200 d-bar-300">
  <dt-text>primary</dt-text>
  <dt-text tone="secondary">secondary</dt-text>
  <dt-text tone="tertiary">tertiary</dt-text>
  <dt-text tone="muted">muted</dt-text>
  <dt-text tone="disabled">disabled</dt-text>
  <dt-text tone="placeholder">placeholder</dt-text>
  <dt-text tone="positive">positive</dt-text>
  <dt-text tone="positive-strong">positive-strong</dt-text>
  <dt-text tone="warning">warning</dt-text>
  <dt-text tone="critical">critical</dt-text>
  <dt-text tone="critical-strong">critical-strong</dt-text>
  <dt-text tone="info">info</dt-text>
  <dt-text tone="info-strong">info-strong</dt-text>
</dt-stack>
</code-example>
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Rather than use the <code v-pre>-inverted</code> tone variants, use the <a href="/components/mode-island.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">v-dt-mode</a> directive.</p>
<code-example source-code='&lt;dt-text v-dt-mode:invert tone=&quot;critical&quot;&gt;critical tone on contrasting surface&lt;/dt-text&gt;'>
<dt-stack gap="100" class="d-py-100 d-px-200 d-bar-300">
  <div class="d-p-100 d-px-150 d-bgc-transparent d-baw2 d-bas-dashed d-bc-subtle">
    <dt-text as="p" align="center" tone="critical">critical tone on default surface</dt-text>
  </div>
  <div class="d-p-100 d-px-150 d-bgc-contrast">
    <dt-text as="p" align="center" v-dt-mode:invert tone="critical">critical tone on contrasting surface</dt-text>
  </div>
</dt-stack>
</code-example>
<h2 id="render-as" tabindex="-1" class="d-docsite--header-2">Render as <a class="header-anchor d-link d-docsite--link d-link" href="#render-as" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>as</code> to declare the underlying HTML tag that the component should render, independent of the visual styling. Defaults to <code v-pre>span</code>.</p>
<code-example source-code='&lt;dt-stack class=&quot;d-w100p&quot; gap=&quot;200&quot;&gt;
  &lt;dt-stack gap=&quot;100&quot;&gt;
    &lt;dt-text kind=&quot;headline&quot; as=&quot;h1&quot; :size=&quot;600&quot;&gt;The Complete Agentic AI Platform&lt;/dt-text&gt;
    &lt;dt-text kind=&quot;body&quot; as=&quot;p&quot; :size=&quot;400&quot;&gt;Our AI Agents come equipped with the core skills businesses need to deliver seamless customer experiences.&lt;/dt-text&gt;
  &lt;/dt-stack&gt;
  &lt;dt-stack direction=&quot;row&quot; gap=&quot;500&quot; align=&quot;start&quot;&gt;
    &lt;dt-stack gap=&quot;100&quot;&gt;
      &lt;dt-text kind=&quot;headline&quot; as=&quot;h2&quot; :size=&quot;500&quot; density=&quot;200&quot;&gt;Try before you AI&lt;/dt-text&gt;
      &lt;dt-text kind=&quot;body&quot; as=&quot;p&quot;&gt;Build, run and optimize your agents - no code, just your expertise and our built-in intelligence.&lt;/dt-text&gt;
    &lt;/dt-stack&gt;
    &lt;dt-stack gap=&quot;100&quot;&gt;
      &lt;dt-text kind=&quot;headline&quot; as=&quot;h2&quot; :size=&quot;500&quot; density=&quot;200&quot;&gt;Great minds sync alike&lt;/dt-text&gt;
      &lt;dt-text kind=&quot;body&quot; as=&quot;p&quot;&gt;Our AI learns and balances speed with quality using one data plane that keeps customers coming back.&lt;/dt-text&gt;
    &lt;/dt-stack&gt;
    &lt;dt-stack gap=&quot;100&quot;&gt;
      &lt;dt-text kind=&quot;headline&quot; as=&quot;h2&quot; :size=&quot;500&quot; density=&quot;200&quot;&gt;History repeats itself. Customers shouldn&#39;t.&lt;/dt-text&gt;
      &lt;dt-text kind=&quot;body&quot; as=&quot;p&quot;&gt;Whatever your customer types or says, our AI and your human agents stay in sync.&lt;/dt-text&gt;
    &lt;/dt-stack&gt;
  &lt;/dt-stack&gt;
&lt;/dt-stack&gt;'>
<dt-stack class="d-w100p" gap="200">
  <dt-stack gap="100">
    <dt-text kind="headline" as="h1" :size="600">The Complete Agentic AI Platform</dt-text>
    <dt-text kind="body" as="p" :size="400">Our AI Agents come equipped with the core skills businesses need to deliver seamless customer experiences.</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="500" align="start">
    <dt-stack gap="100">
      <dt-text kind="headline" as="h2" :size="500" density="200">Try before you AI</dt-text>
      <dt-text kind="body" as="p">Build, run and optimize your agents - no code, just your expertise and our built-in intelligence.</dt-text>
    </dt-stack>
    <dt-stack gap="100">
      <dt-text kind="headline" as="h2" :size="500" density="200">Great minds sync alike</dt-text>
      <dt-text kind="body" as="p">Our AI learns and balances speed with quality using one data plane that keeps customers coming back.</dt-text>
    </dt-stack>
    <dt-stack gap="100">
      <dt-text kind="headline" as="h2" :size="500" density="200">History repeats itself. Customers shouldn't.</dt-text>
      <dt-text kind="body" as="p">Whatever your customer types or says, our AI and your human agents stay in sync.</dt-text>
    </dt-stack>
  </dt-stack>
</dt-stack>
</code-example>
<h2 id="align" tabindex="-1" class="d-docsite--header-2">Align <a class="header-anchor d-link d-docsite--link d-link" href="#align" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Since <code v-pre>DtText</code>'s default element is a <code v-pre>&lt;span&gt;</code>, which is inline by default, the <code v-pre>align</code> prop will only work if its element is styled in a block context.</p>
<code-example source-code='&lt;dt-text align=&quot;start&quot;&gt;....&lt;/dt-text&gt;
&lt;dt-text align=&quot;center&quot;&gt;....&lt;/dt-text&gt;
&lt;dt-text align=&quot;end&quot;&gt;....&lt;/dt-text&gt;
&lt;dt-text align=&quot;justify&quot;&gt;....&lt;/dt-text&gt;'>
<dt-stack class="d-w100p" gap="300">
  <div class="d-bgc-moderate-opaque">
    <dt-text as="p" align="start">Welcome to Dialpad, the most modern, AI-powered business communications platform. We've taken every form of communication that you rely on and unified it into one app. </dt-text>
  </div>
  <div class="d-bgc-moderate-opaque">
    <dt-text as="p" align="center">Welcome to Dialpad, the most modern, AI-powered business communications platform. We've taken every form of communication that you rely on and unified it into one app. </dt-text>
  </div>
  <div class="d-bgc-moderate-opaque">
    <dt-text as="p" align="end">Welcome to Dialpad, the most modern, AI-powered business communications platform. We've taken every form of communication that you rely on and unified it into one app. </dt-text>
  </div>
  <div class="d-bgc-moderate-opaque">
    <dt-text as="p" align="justify">Welcome to Dialpad, the most modern, AI-powered business communications platform. We've taken every form of communication that you rely on and unified it into one app. </dt-text>
  </div>
</dt-stack>
</code-example>
<h2 id="truncate" tabindex="-1" class="d-docsite--header-2">Truncate <a class="header-anchor d-link d-docsite--link d-link" href="#truncate" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Since <code v-pre>DtText</code>'s default element is a <code v-pre>&lt;span&gt;</code>, the <code v-pre>truncate</code> will only work if its element is in block or inline-block context, e.g. <code v-pre>&lt;div&gt;...&lt;/div&gt;</code>.</p>
<code-example source-code='&lt;div class=&quot;d-w-700&quot;&gt;
  &lt;dt-text as=&quot;p&quot; truncate&gt;Welcome to Dialpad, the most modern, AI-powered business communications platform. We&#39;ve taken every form of communication that you rely on and unified it into one app.&lt;/dt-text&gt;
&lt;/div&gt;'>
<div class="d-w-700">
  <dt-text as="p" truncate>Welcome to Dialpad, the most modern, AI-powered business communications platform. We've taken every form of communication that you rely on and unified it into one app.</dt-text>
</div>
</code-example>
<h2 id="max-lines" tabindex="-1" class="d-docsite--header-2">Max Lines <a class="header-anchor d-link d-docsite--link d-link" href="#max-lines" aria-hidden="true">#</a></h2>
<code-example only-show="demo">
<dt-stack gap="100">
  <dt-stack direction="row" gap="200" justify="space-between" align="center">
    <dt-text kind="headline" :size="400" as="h3" tone="secondary">Demo</dt-text>
    <dt-stack direction="row" gap="200" align="center">
      <dt-text v-if="state.isApplied" as="code" kind="code" :size="100" tone="tertiary">
        max-lines="<strong>{{ state.value }}</strong>"
      </dt-text>
      <dt-stack direction="row" gap="25" align="center">
        <dt-stack direction="row">
          <dt-button
            class="d-as-stretch d-bier-0 d-brw0"
            :size="200"
            importance="outlined"
            kind="muted"
            @click="toggleMaxLines"
          >
            Toggle
          </dt-button>
          <dt-button
            v-dt-tooltip="`Decrement`"
            class="d-as-stretch d-g-0 d-bisr-0 d-bier-0 d-brw0"
            :size="200"
            importance="outlined"
            kind="muted"
            :disabled="!canDecreaseMaxLines"
            @click="decrementMaxLines"
          >
            <template #startIcon="{ iconSize }">
              <dt-icon name="dash" :size="iconSize" />
            </template>
          </dt-button>
          <dt-button
            v-dt-tooltip="`Increment`"
            class="d-as-stretch d-g-0 d-bisr-0"
            :size="200"
            importance="outlined"
            kind="muted"
            :disabled="!canIncreaseMaxLines"
            @click="incrementMaxLines"
          >
            <template #startIcon="{ iconSize }">
              <dt-icon name="plus" :size="iconSize" />
            </template>
          </dt-button>
        </dt-stack>
      </dt-stack>
    </dt-stack>
  </dt-stack>
  <dt-stack justify="start" gap="500">
    <dt-text :max-lines="maxLinesBinding" as="p">Welcome to Dialpad, the most modern, AI-powered business communications platform. We've taken every form of communication that you rely on and unified it into one app. Calling a client? Meeting with your team? Texting a colleague? It's all here, on all your devices. AI is by your side to transform your conversations into something you can see and use, giving you and your team a deeper look into action items and insights. Dialpad AI does the legwork to capture the details that matter most while you make and receive calls, send messages, and join meetings in an instant. Welcome to Dialpad, the most modern, AI-powered business communications platform. We've taken every form of communication that you rely on and unified it into one app. Calling a client? Meeting with your team? Texting a colleague? It's all here, on all your devices. AI is by your side to transform your conversations into something you can see and use, giving you and your team a deeper look into action items and insights. Dialpad AI does the legwork to capture the details that matter most while you make and receive calls, send messages, and join meetings in an instant. Welcome to Dialpad, the most modern, AI-powered business communications platform. We've taken every form of communication that you rely on and unified it into one app. Calling a client? Meeting with your team? Texting a colleague? It's all here, on all your devices. AI is by your side to transform your conversations into something you can see and use, giving you and your team a deeper look into action items and insights. Dialpad AI does the legwork to capture the details that matter most while you make and receive calls, send messages, and join meetings in an instant.</dt-text>
  </dt-stack>
</dt-stack>
</code-example>
<code-example only-show="code" source-code='&lt;dt-text as=&quot;p&quot; :max-lines=&quot;4&quot;&gt;....&lt;/dt-text&gt;'>
<dt-text as="p" :max-lines="4">....</dt-text>
</code-example>
<h2 id="wrap" tabindex="-1" class="d-docsite--header-2">Wrap <a class="header-anchor d-link d-docsite--link d-link" href="#wrap" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Control text wrapping behavior. Particularly useful for headlines where balanced line lengths improve readability.</p>
<p class="d-docsite--paragraph">Since <code v-pre>DtText</code>'s default element is a <code v-pre>&lt;span&gt;</code>, which is inline by default, the <code v-pre>wrap</code> prop will only work if its element is styled in a block context.</p>
<code-example source-code='&lt;dt-text&gt;....&lt;/dt-text&gt;
&lt;dt-text wrap=&quot;balance&quot;&gt;....&lt;/dt-text&gt;
&lt;dt-text wrap=&quot;pretty&quot;&gt;....&lt;/dt-text&gt;
&lt;dt-text wrap=&quot;nowrap&quot;&gt;....&lt;/dt-text&gt;'>
<dt-stack gap="400" align="start" class="d-w-700">
  <dt-text as="p"><strong>Default</strong>. Lorem ipsum dolor sit amet consectetur adipisicing consequatur deleniti non doloremque autem adipisci in omnis voluptatibus </dt-text>
  <dt-text as="p" wrap="balance"><strong>Balance</strong>. Lorem ipsum dolor sit amet consectetur adipisicing consequatur deleniti non doloremque autem adipisci in omnis voluptatibus </dt-text>
  <dt-text as="p" wrap="pretty"><strong>Pretty</strong>. Lorem ipsum dolor sit amet consectetur adipisicing consequatur deleniti non doloremque autem adipisci in omnis voluptatibus </dt-text>
  <dt-text as="p" wrap="nowrap"><strong>No Wrap</strong>. Lorem ipsum dolor sit amet consectetur adipisicing consequatur deleniti non doloremque autem adipisci in omnis voluptatibus </dt-text>
</dt-stack>
</code-example>
<h2 id="text-box-trim" tabindex="-1" class="d-docsite--header-2">Text Box Trim <a class="header-anchor d-link d-docsite--link d-link" href="#text-box-trim" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Remove extra leading space above and/or below text. Useful for tight component layouts where text needs to align precisely with adjacent elements.</p>
<p class="d-docsite--paragraph">Text box trim will only affect elements with block or inline-block styled context. It may have no effect on elements with inline or flex context.</p>
<code-example source-code='&lt;dt-text as=&quot;p&quot; text-box-trim=&quot;start&quot;&gt;....&lt;/dt-text&gt;
&lt;dt-text as=&quot;p&quot; text-box-trim=&quot;end&quot;&gt;....&lt;/dt-text&gt;
&lt;dt-text as=&quot;p&quot; text-box-trim=&quot;both&quot;&gt;....&lt;/dt-text&gt;'>
<dt-stack gap="200" :direction="{ 'default': 'column', 'md': 'row' }">
  <dt-text as="p" class="d-bgc-moderate-opaque"><strong>No trim:</strong> lorem ipsum dolor sit amet</dt-text>
  <dt-text as="p" text-box-trim="start" class="d-bgc-moderate-opaque"><strong>Trim start:</strong> lorem ipsum dolor sit amet</dt-text>
  <dt-text as="p" text-box-trim="end" class="d-bgc-moderate-opaque"><strong>Trim end:</strong> lorem ipsum dolor sit amet</dt-text>
  <dt-text as="p" text-box-trim="both" class="d-bgc-moderate-opaque"><strong>Trim both:</strong> lorem ipsum dolor sit amet</dt-text>
</dt-stack>
</code-example>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use <code v-pre>text-box-trim=&quot;both&quot;</code> when text needs to align flush with container's top and/or bottom edges.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Apply text-box-trim to body copy that benefits from natural line spacing.</li>
<li class="d-docsite--list-element">Use text-box-trim as a substitute for proper layout spacing.</li>
</ul>
</template>
</dialtone-usage>
<dt-notice kind="info" title="Browser Support" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Text box trim requires modern browser support for <code v-pre>text-box-trim</code>. Chrome 133+, Edge 132+, Safari 18.2+ as of November 2025. Otherwise, it will gracefully fallback to default rendering with half-leading above and below.
</dt-notice>
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="profile-card" tabindex="-1" class="d-docsite--header-3">Profile Card <a class="header-anchor d-link d-docsite--link d-link" href="#profile-card" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-stack gap=&quot;500&quot;&gt;
  &lt;dt-stack gap=&quot;400&quot; justify=&quot;space-between&quot;&gt;
    &lt;dt-stack&gt;
      &lt;dt-text as=&quot;h2&quot; kind=&quot;headline&quot; :size=&quot;500&quot; strength=&quot;medium&quot; density=&quot;200&quot; class=&quot;d-fs-400&quot;&gt;
        Katie Rodriguez
      &lt;/dt-text&gt;
      &lt;dt-stack direction=&quot;row&quot; gap=&quot;350&quot;&gt;
        &lt;dt-text tone=&quot;positive&quot;&gt;
          Available
        &lt;/dt-text&gt;
        &lt;dt-text&gt;&amp;bull;&lt;/dt-text&gt;
        &lt;dt-text tone=&quot;tertiary&quot;&gt;
          Working from coffee shop
        &lt;/dt-text&gt;
      &lt;/dt-stack&gt;
    &lt;/dt-stack&gt;
    &lt;dt-stack gap=&quot;200&quot;&gt;
      &lt;dt-text kind=&quot;body&quot; :size=&quot;300&quot; density=&quot;200&quot; strength=&quot;semibold&quot; tone=&quot;tertiary&quot;&gt;
        Chief Customer Success Officer
      &lt;/dt-text&gt;
      &lt;dt-text kind=&quot;body&quot; :size=&quot;200&quot; density=&quot;200&quot;&gt;
        &lt;dt-text strength=&quot;semibold&quot;&gt;
          6:19 am
        &lt;/dt-text&gt; local time
      &lt;/dt-text&gt;
    &lt;/dt-stack&gt;
  &lt;/dt-stack&gt;
  &lt;dt-stack gap=&quot;400&quot; direction=&quot;row&quot; class=&quot;d-jc-space-between&quot;&gt;
    &lt;dt-button class=&quot;d-fl1&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon-phone :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
      Call
    &lt;/dt-button&gt;
    &lt;dt-button class=&quot;d-fl1&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon-quick-reply :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
      Message
    &lt;/dt-button&gt;
    &lt;dt-button class=&quot;d-fl1&quot; kind=&quot;muted&quot; importance=&quot;outlined&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon-video :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
      Meet
    &lt;/dt-button&gt;
  &lt;/dt-stack&gt;
&lt;/dt-stack&gt;'>
<ExampleProfileCard />
</code-example>
<h3 id="call-log" tabindex="-1" class="d-docsite--header-3">Call Log <a class="header-anchor d-link d-docsite--link d-link" href="#call-log" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-stack gap=&quot;100&quot; class=&quot;d-w-700&quot;&gt;
  &lt;dt-text as=&quot;h2&quot; kind=&quot;headline&quot; :size=&quot;400&quot;&gt;Saturday, May 24, 2025&lt;/dt-text&gt;
  &lt;dt-stack direction=&quot;row&quot; gap=&quot;150&quot;&gt;
    &lt;dt-avatar full-name=&quot;Ashanti Trevor&quot; /&gt;
    &lt;dt-stack class=&quot;d-fl1&quot;&gt;
      &lt;dt-text kind=&quot;body&quot; :size=&quot;200&quot; strength=&quot;bold&quot;&gt;Ashanti Trevor&lt;/dt-text&gt;
      &lt;dt-stack direction=&quot;row&quot; gap=&quot;50&quot;&gt;
        &lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot;&gt;
          &lt;dt-icon name=&quot;phone-outgoing&quot; size=&quot;200&quot; class=&quot;d-fc-tertiary&quot; /&gt;
          &lt;dt-text kind=&quot;body&quot; :size=&quot;100&quot; tone=&quot;tertiary&quot;&gt;Outgoing call&lt;/dt-text&gt;
        &lt;/dt-stack&gt;
        &lt;dt-text kind=&quot;body&quot; :size=&quot;100&quot; tone=&quot;tertiary&quot;&gt;&amp;bull;&lt;/dt-text&gt;
        &lt;dt-text kind=&quot;body&quot; :size=&quot;100&quot; tone=&quot;tertiary&quot;&gt;2 minutes 10 seconds&lt;/dt-text&gt;
      &lt;/dt-stack&gt;
    &lt;/dt-stack&gt;
    &lt;dt-text kind=&quot;body&quot; :size=&quot;200&quot; tone=&quot;tertiary&quot;&gt;3:23 pm&lt;/dt-text&gt;
    &lt;dt-badge kind=&quot;count&quot; type=&quot;bulletin&quot; text=&quot;6&quot; /&gt;
  &lt;/dt-stack&gt;
&lt;/dt-stack&gt;'>
<dt-stack gap="100" class="d-w-700">
  <dt-text as="h2" kind="headline" :size="400">Saturday, May 24, 2025</dt-text>
  <dt-stack direction="row" gap="150">
    <dt-avatar full-name="Ashanti Trevor" />
    <dt-stack class="d-fl1">
      <dt-text kind="body" :size="200" strength="bold">Ashanti Trevor</dt-text>
      <dt-stack direction="row" gap="50">
        <dt-stack direction="row" gap="100">
          <dt-icon name="phone-outgoing" size="200" class="d-fc-tertiary" />
          <dt-text kind="body" :size="100" tone="tertiary">Outgoing call</dt-text>
        </dt-stack>
        <dt-text kind="body" :size="100" tone="tertiary">&bull;</dt-text>
        <dt-text kind="body" :size="100" tone="tertiary">2 minutes 10 seconds</dt-text>
      </dt-stack>
    </dt-stack>
    <dt-text kind="body" :size="200" tone="tertiary">3:23 pm</dt-text>
    <dt-badge kind="count" type="bulletin" text="6" />
  </dt-stack>
</dt-stack>
</code-example>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Maintain semantic structure via <code v-pre>as</code> (e.g., screen readers expect heading levels to be sequential).</li>
<li class="d-docsite--list-element">When using <code v-pre>truncate</code>, provide another way to access the full content (tooltip, detail view, or explicit <code v-pre>aria-label</code>). <code v-pre>DtText</code> does not apply alternative access to the full string, so consuming applications should opt in.</li>
<li class="d-docsite--list-element">Allow numeric content to remain readable by enabling the <code v-pre>numeric</code> prop when aligning tables or numbers that dynamically update.</li>
</ul>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="text" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="text"></component-class-table></div></template>


<script setup>
import { computed, reactive } from 'vue';
import { TEXT_SIZE_MODIFIERS } from '@dialpad/dialtone-vue';
import ExampleProfileCard from '@exampleComponents/ExampleProfileCard.vue';

const textSizeColumns = [100, 200, 300, 400, 500, 600, 700];
const textSizeKinds = [
  { name: 'headline', as: 'h2', sizes: TEXT_SIZE_MODIFIERS.headline },
  { name: 'body', as: 'p', sizes: TEXT_SIZE_MODIFIERS.body },
  { name: 'label', as: 'p', sizes: TEXT_SIZE_MODIFIERS.label },
  { name: 'code', as: 'code', sizes: TEXT_SIZE_MODIFIERS.code, class: 'd-bgc-transparent', tone: 'secondary' },
];

const BOUNDS = Object.freeze({ min: 2, max: 10, default: 4 });
const clampToBounds = (value) => Math.min(Math.max(value ?? BOUNDS.default, BOUNDS.min), BOUNDS.max);

const state = reactive({
  value: clampToBounds(BOUNDS.default),
  lastApplied: clampToBounds(BOUNDS.default),
  isApplied: true,
});

const maxLinesBinding = computed(() => (state.isApplied ? state.value : undefined));

const canDecreaseMaxLines = computed(() => state.isApplied && state.value > BOUNDS.min);
const canIncreaseMaxLines = computed(() => state.isApplied && state.value < BOUNDS.max);

const adjustMaxLines = (delta) => {
  if (!state.isApplied) return;
  const nextValue = clampToBounds(state.value + delta);
  if (nextValue === state.value) return;

  state.value = nextValue;
  state.lastApplied = nextValue;
};

const decrementMaxLines = () => adjustMaxLines(-1);
const incrementMaxLines = () => adjustMaxLines(1);

const toggleMaxLines = () => {
  if (state.isApplied) {
    state.lastApplied = state.value;
    state.isApplied = false;
    return;
  }

  const restoredValue = clampToBounds(state.lastApplied);
  state.value = restoredValue;
  state.lastApplied = restoredValue;
  state.isApplied = true;
};
</script>